import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_villa-booking-system-1/artifacts/suj9ewkw_65DC7DEC-C9BA-4743-8DA2-CAD0C86FB49C.png";

const links = [
  { label: "Home", to: "/", anchor: "top" },
  { label: "Schedule", to: "/schedule", route: true },
  { label: "Gallery", to: "/", anchor: "gallery" },
  { label: "Amenities", to: "/", anchor: "amenities" },
  { label: "Contact", to: "/", anchor: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goLink = (link) => {
    setOpen(false);
    if (link.route) {
      navigate(link.to);
      return;
    }
    goTo(link.anchor);
  };

  const goTo = (anchor) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { anchor } });
      return;
    }
    if (anchor === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goBookNow = () => {
    setOpen(false);
    navigate("/schedule");
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 flex items-center justify-between gap-3">
        <button
          onClick={() => goTo("top")}
          data-testid="nav-logo"
          className="flex items-center gap-2 md:gap-3 group min-w-0"
        >
          <img
            src={LOGO_URL}
            alt="Ibiza Poolside Villa Resort"
            className="h-10 sm:h-12 md:h-14 w-auto rounded-full object-contain transition-transform duration-500 group-hover:scale-105 flex-shrink-0"
          />
          <span className="hidden md:inline font-serif-display text-base md:text-lg tracking-[0.18em] uppercase text-[#C5A059] truncate">
            Ibiza Poolside
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => goLink(l)}
              className="luxury-link text-sm font-sans-ui"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <Button
            data-testid="nav-book-now-button"
            onClick={goBookNow}
            className="gold-btn rounded-full px-6 py-2 text-sm"
          >
            Book Now
          </Button>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass mt-3 mx-4 rounded-xl p-6 flex flex-col gap-4">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => goLink(l)}
              className="text-left luxury-link"
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <Button
            onClick={goBookNow}
            className="gold-btn rounded-full w-full"
            data-testid="mobile-nav-book-now"
          >
            Book Now
          </Button>
        </div>
      )}
    </header>
  );
}
